/**
 * useBuildTaskDetail · 按 taskId 拉 build_task 详情, 非终态自动 3s 轮询
 *
 * 职责: 纯查询服务 — 给个 taskId, 返回最新 detail。不负责任何业务策略
 * (如刷 FolderList / 弹 toast / 埋点等), 调用方拿到 detail 自己按需消费。
 *
 * 设计目标:
 *   · 替代旧的"顶层 useActiveBuildTaskPolling + Redux 中转 status"链路
 *   · 把任务状态的生命周期跟 BuildTaskStatusMessage 组件实例绑定 — 组件挂
 *     就开始拉, 组件卸了就停, 组件被 messages 清空带走自然回收
 *   · 多任务历史会话每条 message 各自挂一个 hook → 并行请求、先到先显示,
 *     不被 Promise.all 阻塞
 *
 * 协议:
 *   POST /web/workgroup/folder/ai/chat/task body: { task_id }
 *   resp: BuildTaskDetail (含 top_folders 全嵌套树, 历史详情走它做 N 真相)
 *
 * 生命周期:
 *   1. mount / taskId 变化 → 立即拉一次 (不等 3s tick), 让首次渲染尽早脱离 loading
 *   2. 拿到 detail.status 非终态 (pending/running) → 3s setTimeout 再拉
 *   3. 终态 (done/failed/canceled) → 停止轮询; detail 保留, UI 渲染最终态
 *   4. unmount / taskId 切换 → 取消定时器 + 标记 cancelled
 *   5. 错误累计 MAX_ERRORS 次 → 放弃 (跟旧 polling hook 兜底一致), detail 维持上一次
 *
 * 不进 Redux 的原因:
 *   消费方就 BuildTaskStatusMessage 一处, 其他组件不需要"任务状态"这一信息;
 *   局部 state 比 store 中转更直接, 还省掉一个 reducer。
 */

import { useEffect, useRef, useState } from 'react'
import { useAppSelector } from '../../../app/hooks'
import { getBuildTask } from '../services/sessionApi'
import type { BuildTaskDetail } from '../../../types/ai-classify'

const POLL_INTERVAL_MS = 3000
const MAX_ERRORS = 3
// 终态语义跟旧 polling hook 对齐 — pending/running 之外都算终态
const TERMINAL_STATUSES: ReadonlySet<BuildTaskDetail['status']> = new Set<
  BuildTaskDetail['status']
>(['done', 'failed', 'canceled'])

export interface UseBuildTaskDetailResult {
  /** 任务详情 · 首次未拿到 / 累计失败时为 null */
  detail: BuildTaskDetail | null
  /** true = 首次请求在飞行中 (后续轮询不重置 loading, 避免 UI 闪烁) */
  loading: boolean
}

export function useBuildTaskDetail(taskId: string): UseBuildTaskDetailResult {
  // groupID 取自 RTK group 树（pojo 直读）
  const groupID = useAppSelector((s) => s.group.groupId)

  const [detail, setDetail] = useState<BuildTaskDetail | null>(null)
  const [loading, setLoading] = useState(true)

  // setTimeout 句柄 + 错误计数 · ref 避免引发 re-render
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const errorCountRef = useRef(0)

  useEffect(() => {
    function clearTimer(): void {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }

    if (!taskId || !groupID) {
      clearTimer()
      setLoading(false)
      return
    }

    let cancelled = false
    // taskId 切换时重置 loading / 错误计数, 让新任务跑首次拉取的视觉
    errorCountRef.current = 0
    setLoading(true)

    async function tick(): Promise<void> {
      if (cancelled) return
      try {
        const t = await getBuildTask(groupID, taskId)
        if (cancelled) return
        errorCountRef.current = 0
        setDetail(t)
        setLoading(false)

        if (TERMINAL_STATUSES.has(t.status)) {
          // 终态 → 停止轮询; detail 已写, UI 渲染最终态
          clearTimer()
          return
        }
        // 非终态 → 排下一次
        timerRef.current = setTimeout(() => {
          void tick()
        }, POLL_INTERVAL_MS)
      } catch {
        if (cancelled) return
        errorCountRef.current += 1
        if (errorCountRef.current >= MAX_ERRORS) {
          // 累计失败 → 放弃; setLoading(false) 让 UI 退出 loading 态 (detail 维持 null)
          // BuildTaskStatusMessage 看到 detail==null 时返回 null 不挂卡, 避免卡在 loading
          clearTimer()
          setLoading(false)
          return
        }
        // 失败重排, 不影响 detail / loading
        timerRef.current = setTimeout(() => {
          void tick()
        }, POLL_INTERVAL_MS)
      }
    }

    void tick()

    return () => {
      cancelled = true
      clearTimer()
    }
  }, [taskId, groupID])

  return { detail, loading }
}
